"use client";

import { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import posthog from "posthog-js";

import type { JanoshikReport } from "@/config/janoshik-reports";

import { JanoshikReportModal } from "./janoshik-report-modal";
import { JanoshikModalStateContext } from "./janoshik-modal-state-context";

/** Optional client-side observer, including around a server-rendered report card. */
export const JanoshikModalOpenContext = createContext<(() => void) | undefined>(undefined);

/** Which element opened the report; joins the click to the dead-click signal per surface. */
type JanoshikReportSurface = "card" | "chip" | "home" | "lp";

interface JanoshikModalTriggerProps {
	report: JanoshikReport;
	ariaLabel: string;
	/** Product page the trigger sits on, when it sits on one; the report is keyed by this slug. */
	productSlug?: string;
	/** Omitted by legacy callers; reported as "other" so the event stays countable. */
	surface?: JanoshikReportSurface;
	className?: string;
	children: ReactNode;
}

export function JanoshikModalTrigger({
	report,
	ariaLabel,
	productSlug,
	surface,
	className,
	children,
}: JanoshikModalTriggerProps) {
	const [open, setOpen] = useState(false);
	const onOpen = useContext(JanoshikModalOpenContext);
	const onStateChange = useContext(JanoshikModalStateContext);
	const close = useCallback(() => {
		setOpen(false);
		onStateChange?.(false);
	}, [onStateChange]);
	const openReport = () => {
		if (open) return;
		setOpen(true);
		onStateChange?.(true);
		onOpen?.();
		// PostHog's dead-click detector flags 86 persons per 4 weeks tapping the
		// DE Retatrutide report card without a DOM mutation within 2.75 s, but it
		// cannot tell a real no-op from a late modal. This open event is the
		// counterpart: opens per session vs. dead clicks per session decides
		// whether the card is broken on some browsers or the detector is noisy.
		try {
			posthog.capture("janoshik_report_opened", {
				product_slug: productSlug ?? null,
				surface: surface ?? "other",
				task_number: report.taskNumber,
			});
		} catch {
			// Telemetry must never keep the report from opening.
		}
	};
	return (
		<>
			<button
				type="button"
				onClick={openReport}
				aria-label={ariaLabel}
				aria-haspopup="dialog"
				className={className}
			>
				{children}
			</button>
			{open ? <JanoshikReportModal report={report} onClose={close} /> : null}
		</>
	);
}
